<script setup>
import { computed, h } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

import {
  DropdownContainer,
  DropdownBody,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAutoOffline = useMapGetter('getCurrentUserAutoOffline');

const { isImpersonating } = useImpersonation();

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

const activeStatus = computed(() => {
  return availabilityStatuses.value.find(status => status.active);
});

const autoOfflineToggle = computed({
  get: () => currentUserAutoOffline.value,
  set: autoOffline => {
    store.dispatch('updateAutoOffline', {
      accountId: currentAccountId.value,
      autoOffline,
    });
  },
});

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}
</script>

<template>
  <div class="grid gap-1 px-1 py-1">
    <DropdownContainer>
      <template #trigger="{ toggle }">
        <Button
          size="sm"
          color="slate"
          variant="faded"
          icon="i-lucide-chevron-down"
          trailing-icon
          @click="toggle"
        >
          <div class="flex gap-1.5 items-center text-sm">
            <span
              class="size-2 flex-shrink-0 rounded-full"
              :class="activeStatus.color"
            />
            <span>{{ activeStatus.label }}</span>
          </div>
        </Button>
      </template>
      <DropdownBody class="min-w-32 z-20" strong>
        <DropdownItem
          v-for="status in availabilityStatuses"
          :key="status.value"
          :label="status.label"
          :icon="status.icon"
          class="cursor-pointer"
          :click="() => changeAvailabilityStatus(status.value)"
        />
      </DropdownBody>
    </DropdownContainer>
    <div class="flex items-center justify-between gap-2 min-h-8 px-1">
      <div class="flex min-w-0 items-center gap-1 text-sm text-n-slate-12">
        <span class="truncate">
          {{ $t('SIDEBAR.SET_AUTO_OFFLINE.SHORT') }}
        </span>
        <Icon
          v-tooltip.top="$t('SIDEBAR.SET_AUTO_OFFLINE.INFO_SHORT')"
          icon="i-lucide-info"
          class="size-3.5 flex-shrink-0 text-n-slate-10"
        />
      </div>
      <ToggleSwitch v-model="autoOfflineToggle" />
    </div>
  </div>
</template>
